import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Form } from '@nucleus-commerce/ui/components/form';

import { fromForms, toFormsPatch } from '../api/mappers';
import { useChatSettingsForm, type ChatSettingsTabProps } from '../hooks/use-chat-settings-form';
import { formsSchema, type FormsForm } from '../schemas/forms-schema';

import { FormAlert, SaveButton, SwitchField, TextField } from './form-controls';
import { FormFieldsBuilder } from './form-fields-builder';

const GROUPS = ['preChat', 'offlineForm'] as const;

/** The pre-chat form (before a chat starts) and the offline form (when nobody can answer). */
export function FormsTab({ storeId, settings, readOnly }: ChatSettingsTabProps) {
  const { t } = useTranslation('chatSettings');
  const { form, submit, saving } = useChatSettingsForm<FormsForm>({
    storeId,
    schema: formsSchema,
    defaultValues: fromForms(settings),
    toPatch: toFormsPatch,
    groups: GROUPS,
    readOnly,
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex max-w-3xl flex-col gap-6">
        <FormAlert message={form.formState.errors.root?.server?.message} />
        <FormSection title={t('forms.preChat.title')} description={t('forms.preChat.description')}>
          <SwitchField
            control={form.control}
            name="preChat.enabled"
            label={t('forms.preChat.enabled')}
            disabled={readOnly}
          />
          <FormFieldsBuilder list="preChat" disabled={readOnly} />
        </FormSection>
        <FormSection
          title={t('forms.offlineForm.title')}
          description={t('forms.offlineForm.description')}
        >
          <SwitchField
            control={form.control}
            name="offlineForm.enabled"
            label={t('forms.offlineForm.enabled')}
            description={t('forms.offlineForm.enabledHint')}
            disabled={readOnly}
          />
          <FormFieldsBuilder list="offlineForm" disabled={readOnly} />
          <TextField
            control={form.control}
            name="offlineForm.confirmationText"
            label={t('forms.offlineForm.confirmation')}
            multiline
            disabled={readOnly}
          />
        </FormSection>
        <SaveButton saving={saving} dirty={form.formState.isDirty} hidden={readOnly} />
      </form>
    </Form>
  );
}
